import {Box, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

/** The smallest content box, also where a filling one stops shrinking (the dialog scrolls below it). */
const EMAIL_CONTENT_MIN_HEIGHT = 280;

/** Both source and preview keep the same viewport, including for very long emails. */
export const EMAIL_CONTENT_HEIGHT = `clamp(${EMAIL_CONTENT_MIN_HEIGHT}px, 45vh, 560px)`;

/** Enlarged dialog: the window height minus ~570px of measured chrome and fields, never below the normal height. */
export const EMAIL_CONTENT_HEIGHT_EXPANDED = `max(${EMAIL_CONTENT_HEIGHT}, calc(100vh - 570px))`;

/** A builder can export a full document containing only empty layout tables. */
function isEmptyEmail(content: string): boolean {
    if (!content.trim()) return true;
    const document = new DOMParser().parseFromString(content, 'text/html');
    // Avoid placing the empty-state label over images or CSS decorations.
    const hasPaint = /url\s*\(|\bcontent\s*:/i.test(content) ||
        Array.from(document.querySelectorAll('style')).some(element =>
            /\bborder(?:-(?:top|right|bottom|left))?(?:-width)?\s*:/i.test(element.textContent || '')) ||
        Array.from(document.querySelectorAll<HTMLElement>('[style]')).some(element =>
            ['top', 'right', 'bottom', 'left'].some(side => {
                const border = element.style.getPropertyValue(`border-${side}-style`);
                const width = element.style.getPropertyValue(`border-${side}-width`);
                return border && border !== 'none' && parseFloat(width) !== 0;
            }));
    document.querySelectorAll('style, script, template').forEach(element => element.remove());
    return !hasPaint && !document.body.textContent?.trim() &&
        !document.body.querySelector('img, picture, svg, canvas, video, audio, iframe, object, embed, hr, input, select, textarea, [background]');
}

/** `fill`: takes the rest of a flex column instead of keeping `height`, never below the smallest normal height. */
export default function EmailContentPreview({content, height = EMAIL_CONTENT_HEIGHT, fill}: {content: string; height?: string; fill?: boolean}) {
    const {t} = useTranslation();
    const empty = useMemo(() => isEmptyEmail(content), [content]);
    return <Box sx={theme => ({position: 'relative', overflow: 'hidden', borderRadius: 1,
        ...(fill ? {flex: '1 1 0', minHeight: EMAIL_CONTENT_MIN_HEIGHT} : {height}),
        bgcolor: alpha(theme.palette.text.primary, 0.045)})}>
        {content.trim() && <Box component={'iframe'} title={t('Email preview')} srcDoc={content} sandbox={''}
                              referrerPolicy={'no-referrer'}
                              sx={{position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%', border: 0, bgcolor: 'common.white'}}/>}
        {empty && <Box sx={{position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none'}}>
            <Typography variant={'body1'} color={'text.secondary'}>{t('Preview')}</Typography>
        </Box>}
    </Box>;
}
